import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { promoCodeService } from '../services/firebase'; // ✅ ADDED: Import the real Firebase service

const CartContext = createContext();

/**
 * BRAND NOTIFICATION HELPER
 * Matches the Light Editorial aesthetic: Sharp corners, legible sizes, and subtle left-border accents.
 */
const notify = (message, type = 'success') => {
  const baseStyle = {
    borderRadius: '0px',
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    fontFamily: 'inherit',
    fontWeight: '600',
    padding: '14px 16px',
    background: '#ffffff',
    color: '#000000',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.05)',
  };

  if (type === 'success') {
    toast.success(message, {
      style: { ...baseStyle, borderLeft: '4px solid #ec4899', color: '#be185d' },
      icon: false,
      progressStyle: { background: '#ec4899' }
    });
  } else if (type === 'error') {
    toast.error(message, {
      style: { ...baseStyle, borderLeft: '4px solid #000000', color: '#000000' },
      icon: false,
      progressStyle: { background: '#000000' }
    });
  } else {
    toast.info(message, {
      style: { ...baseStyle, borderLeft: '4px solid #a1a1aa', color: '#525252' },
      icon: false,
      progressStyle: { background: '#d4d4d4' }
    });
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [promoCode, setPromoCode] = useState(null);
  const [discount, setDiscount] = useState(0);

  // ========== PERSISTENCE WITH SAFE LOADING ==========
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('vibrant_noir_selection');
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        const validCart = parsed.filter(item => 
          item && typeof item.id === 'string' && item.id.trim() !== ''
        );
        
        if (validCart.length !== parsed.length) {
          console.warn('🧹 Removed invalid cart items (missing or empty id)');
          notify('Some items were removed from your selection', 'info');
        }
        
        setCart(validCart);
      }
    } catch (error) {
      console.error('❌ Failed to load cart from localStorage:', error);
      localStorage.removeItem('vibrant_noir_selection');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('vibrant_noir_selection', JSON.stringify(cart));
  }, [cart]);

  // ========== CART ACTIONS WITH GUARDS ==========
  const addToCart = (product, quantity = 1) => {
    if (!product || !product.id) {
      console.error('❌ Cannot add product without ID to cart', product);
      notify('Error adding item', 'error');
      return;
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });

    notify(`Added to Selection: ${product.name}`);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    notify('Removed from Selection', 'info');
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setPromoCode(null);
    setDiscount(0);
  };

  // ========== CALCULATIONS ==========
  const getCartTotal = () =>
    cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const getCartCount = () =>
    cart.reduce((count, item) => count + item.quantity, 0);

  const getGrandTotal = () => Math.max(0, getCartTotal() - discount);

  // ========== PROMO CODES ==========
  const applyPromoCode = async (code) => {
    const total = getCartTotal();
    const response = await validatePromoCode(code, total);

    if (response.valid) {
      const { discountType, discountValue } = response.data;
      const amount = discountType === 'percentage'
        ? total * (discountValue / 100)
        : discountValue;

      setPromoCode(response.data);
      setDiscount(amount);
      notify(`Promo Applied: ₦${amount.toLocaleString()}`);
      return { success: true };
    }

    notify(response.error, 'error');
    return { success: false };
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setDiscount(0);
    notify('Promo Removed', 'info');
  };

  // ✅ SECURED: Now calls the real Firebase service instead of using hardcoded mock codes!
  const validatePromoCode = async (code, orderAmount) => {
    try {
      const result = await promoCodeService.validatePromoCode(code, orderAmount);
      return result;
    } catch (error) {
      console.error('Error validating promo code:', error);
      return { valid: false, error: 'Error validating promo code' };
    }
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    applyPromoCode,
    removePromoCode,
    promoCode,
    discount,
    getGrandTotal
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};